import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { capture } from "@/lib/posthog";
import { useReferralTracking } from "@/hooks/useReferralTracking";

const Signup = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getStoredReferral, clearReferral } = useReferralTracking();

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate("/edit-profile?newUser=true");
      }
    };
    checkSession();
  }, [navigate]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/edit-profile?newUser=true`,
        },
      });

      if (error) {
        trackEvent(ANALYTICS_EVENTS.SIGN_UP, { 
          success: false, 
          error: error.message,
          provider: 'email'
        });
        toast({
          variant: "destructive",
          title: "Signup failed",
          description: error.message,
        });
      } else if (data.user) {
        // PostHog marketing event for funnel
        capture('sign_up', { provider: 'email' });
        
        // Handle referral attribution
        const referralData = getStoredReferral();
        if (referralData) {
          try {
            
            // Set the referred_by column directly in the users table
            const { error: updateError } = await supabase
              .from('users')
              .update({ 
                referred_by_affiliate_code: referralData.code.toLowerCase() 
              })
              .eq('id', data.user.id);

            if (updateError) {
              
              // Fallback to the old referral system if direct update fails
              const { error: attributionError } = await supabase.functions.invoke('affiliate-management/create-referral', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  affiliateCode: referralData.code,
                  referredUserId: data.user.id,
                  signupData: {
                    email: data.user.email,
                    signupMethod: 'email',
                    timestamp: new Date().toISOString()
                  }
                })
              });

              if (attributionError) {
              } else {
              }
            } else {
              clearReferral(); // Clear the stored referral after successful attribution
            }
          } catch (error) {
          }
        }
        
        trackEvent(ANALYTICS_EVENTS.SIGN_UP, { 
          success: true,
          provider: 'email',
          hasReferral: !!referralData
        });
        toast({
          title: "Success",
          description: "Please check your email to verify your account",
        });
        navigate("/edit-profile?newUser=true");
      }
    } catch (error) {
      trackEvent(ANALYTICS_EVENTS.SIGN_UP, { 
        success: false,
        provider: 'email',
        error: 'Unexpected error'
      });
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setGoogleLoading(true);
    try {
      trackEvent(ANALYTICS_EVENTS.SIGN_UP, { 
        provider: 'google',
        status: 'initiated'
      });
      // PostHog marketing event (initiated)
      capture('sign_up', { provider: 'google', status: 'initiated' });
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/edit-profile?newUser=true`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        }
      });

      if (error) {
        trackEvent(ANALYTICS_EVENTS.SIGN_UP, { 
          success: false,
          provider: 'google',
          error: error.message
        });
        toast({
          variant: "destructive",
          title: "Signup failed",
          description: "Failed to sign up with Google. Please try again.",
        });
      }
    } catch (error) {
      trackEvent(ANALYTICS_EVENTS.SIGN_UP, { 
        success: false,
        provider: 'google',
        error: 'Unexpected error'
      });
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="container max-w-md mx-auto px-4 py-12 animate-fade-in">
      <h1 className="text-4xl font-bold mb-2 tracking-tight">Create an Account</h1>
      <p className="text-muted-foreground mb-8">Get started with Posit today</p>
      <div className="space-y-6">
        <Button 
          variant="outline" 
          type="button" 
          className="w-full flex items-center justify-center gap-2 h-11 text-base font-medium shadow-sm hover:shadow-md transition-all duration-200" 
          onClick={handleGoogleSignup}
          disabled={googleLoading}
        >
          {googleLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          )}
          Sign up with Google
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase tracking-wider font-medium">
            <span className="bg-background px-2 text-muted-foreground">
              Or continue with
            </span>
          </div>
        </div>

        <form onSubmit={handleSignup} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>
          <Button type="submit" className="w-full h-11 text-base font-medium shadow-sm hover:shadow-md transition-all duration-200 text-white" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating account...
              </>
            ) : (
              "Create Account"
            )}
          </Button>
        </form>
        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="text-primary hover:underline">
            Log in here
          </Link>
        </p>
        <p className="text-center text-xs text-muted-foreground mt-4">
          By signing up, you agree to our{" "}
          <Link to="/terms" className="text-primary hover:underline">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;